import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { DRAG_TYPE, DropCallback } from "../../stores/reducers/user-interface";
import { VideoStream } from "../types/videos";
import useDropArea from "../../hooks/use-dnd/use-drop-area";
import blackImage from '../../images/black-image.jpeg';
import { useSelector } from "react-redux";
import { GreenroomState as GS } from '../../stores/types/types';
import { appLogger } from '../../utils/datadog';
import classNames from "classnames";
import { AVContext } from "../../contexts/audio-video";
import { contrastingColor } from "../../utils/utils";
import { cleanupVideoStreamElement } from "../../utils/video/video-streams";
import useShotMemos from "../../hooks/use-shot-memos";
import { ROOM_STATES } from "../../stores/reducers/settings";
import styled from "styled-components";
import Portal from '../general-components/portal';
import SipCallHoverOptions from "./sip-call-hover-options";
import { videoGridClasses } from "../../types/constants";
import V_LAYOUTS from "../../utils/video-layout";
import colors from '../../utils/colors';

const VideoContainer = styled.div`
	height: 100%;
	width: 100%;
	display: flex;
	justify-content: center;
`;

const ExpandContainer = styled.div`
	height: 100%;

	&.wide {
		height: auto;
		width: 100%;
	}

	&.expanded {
		width: auto;
		aspect-ratio: 16/9;
	}
`;

const ASPECT_RATIO_16_9 = 16 / 9;
const ASPECT_RATIO_4_3 = 4 / 3;

interface Props {
	video: VideoStream | undefined;
	feedPosition: number;
	dropCallback: DropCallback;
	audioOnly?: boolean;
	isForShotEditorModal: boolean;
	expanded: boolean | undefined;
	toggleExpandedCallback: (sipStreamId: string) => void;
}

export default function FeedSipCall(props: Props) {
	const {
		video,
		feedPosition,
		dropCallback,
		audioOnly,
		isForShotEditorModal,
		expanded,
		toggleExpandedCallback,
	} = props;

	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const embed = useSelector((s: GS) => s.SessionReducer.embed);
	const backstageCanvasVolume = useSelector((s: GS) => s.MeetingReducer.backstageCanvasVolume);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);
	const host = useSelector((state: GS) => state.SessionReducer.host);
	const [isHovering, setIsHovering] = useState<boolean>(false);
	const shotModalShot = useSelector((s: GS) => s.ShotsReducer.shotModalShot);
	const [isWideView, setIsWideView] = useState<boolean>(false);
	const [isExpandable, setIsExpandable] = useState<boolean>(false);

	const videoElRef = useRef<HTMLVideoElement>(null);
	const { getAV } = useContext(AVContext);
	const { video: hasVideo } = getAV(video?.id);

	const { isOnCanvas, currentShot } = useShotMemos();

	const {
		shot_data: {
			forcedLayout,
		},
	} = isForShotEditorModal ? shotModalShot : currentShot;

	const colorStyles = useMemo(() => {
		const colorObj = {
			backgroundColor: '#333',
			color: contrastingColor(colors.gray33),
		};

		if (meeting?.primary_color) {
			colorObj.backgroundColor = `#${meeting.primary_color}`;
			colorObj.color = contrastingColor(meeting.primary_color);
		}

		return colorObj;
	}, [meeting]);

	const { isOver, dropRef } = useDropArea({
		accepts: DRAG_TYPE.FEEDS,
		dropCallback,
		dropAreaData: { feedPosition },
	});

	async function playPresenterFeed(srcObject: MediaStream) {
		if (!videoElRef.current) return;

		videoElRef.current.srcObject = srcObject;
		videoElRef.current.volume = 0;
		if (!srcObject.active) return;

		try {
			await videoElRef.current.play();
			if (videoElRef.current?.paused) {
				try {
					await videoElRef.current.play();
				} catch (e: any) {
					appLogger.error(
						'video el play request error',
						{ location: 'feed-presenter second' },
						e,
					);
				}
			}
		} catch (e: any) {
			appLogger.error(
				'video el play request error',
				{ location: 'feed-presenter first' },
				e,
			);
		}

		cleanupVideoStreamElement(videoElRef.current);
	}

	const videoDebounce = useRef<NodeJS.Timeout | null>(null);

	const isHiddenTwoUpShot = useMemo(
		(): boolean => feedPosition === 0
			&& forcedLayout === V_LAYOUTS.TWO.FULL_TWO,
		[forcedLayout, feedPosition],
	);

	const videoContainerRef = useRef<HTMLDivElement>(null);

	// This attaches connectedPresenterVideo.srcObject to our video DOM element.
	// The srcObject is derived from OpenTok's publisher/subscriber object.
	// This has been known to cause issues where the user's video does not appear.
	// (Presenter appears as a black feed instead.)
	// green-4902
	useEffect(() => {
		if (videoElRef.current && video && video?.srcObject !== videoElRef.current.srcObject) {
			if (videoDebounce.current) { clearTimeout(videoDebounce.current); }
			videoDebounce.current = setTimeout(() => {
				playPresenterFeed(video.srcObject);
			}, 100);
		}
	}, [video?.srcObject, videoElRef.current]);

	useEffect(() => {
		if (videoElRef.current) {
			videoElRef.current.volume = (
				(isOnCanvas || embed || roomState === ROOM_STATES.EDITING)
					? 1
					: backstageCanvasVolume
			);
		}
	}, [isOnCanvas, roomState, backstageCanvasVolume]);

	const setViewSize = () => {
		if (videoContainerRef.current) {
			const rect = videoContainerRef.current.getBoundingClientRect();
			setIsWideView((rect.width / rect.height) > ASPECT_RATIO_4_3);
			setIsExpandable((rect.width / rect.height) < ASPECT_RATIO_16_9);
		}
	};

	const resizeObserver = new ResizeObserver(() => { setViewSize() });

	useEffect(() => {
		if (videoContainerRef.current) {
			resizeObserver.observe(videoContainerRef.current);
		}
		setViewSize();
		return () => {
			if (videoContainerRef.current) {
				resizeObserver.unobserve(videoContainerRef.current);
			}
		};
	}, []);

	return (
		<div ref={dropRef} className='feed-drop-area'
			onMouseEnter={() => setIsHovering(true)}
			onMouseLeave={() => setIsHovering(false)}
		>
			<VideoContainer ref={videoContainerRef}>
				{video && video.srcObject && video?.srcObject.active
					? <>
						{isOver && <div className="dnd-highlighted" />}
						<ExpandContainer className={classNames(isWideView && 'wide', expanded && 'expanded')}>
							<video
								key={video.srcObject.id + feedPosition}
								muted={true}
								poster={blackImage}
								autoPlay={true}
								playsInline
								ref={videoElRef}
								onLoadedData={() => {
									// Force play on presenter video elements to avoid a
									// paused OpenTok publisher/subscriber feed.
									// green-4902
									videoElRef.current?.play().catch((e) => {
										appLogger.error('video el play request error', {
											location: 'feed-presenter third',
										}, e);
									});
								}}
							>
							</video>
						</ExpandContainer>

						<div className={classNames('poster', { active: !hasVideo })}>
							{video.poster 
								? <img className="poster-image" src={video.poster} alt={''} />
								: <div className="no-image-placeholder">
									<span style={colorStyles}>
										{video.displayName}
									</span>
								</div>
							}
						</div>

						{(host && isExpandable) && (
							<Portal targetId="video-hover-options-portal">
								<SipCallHoverOptions
									sipStreamId={video.id}
									isHovering={isHovering}
									videoRef={videoContainerRef.current}
									isForShotEditorModal={isForShotEditorModal}
									expanded={expanded}
									toggleExpandedCallback={toggleExpandedCallback}
									className={classNames(
										'width-full',
										'height-full',
										videoGridClasses[feedPosition],
										{
											'hidden-feed': isHiddenTwoUpShot,
											'display-none': audioOnly,
										},
									)}
								/>
							</Portal>
						)}
					</>
					: <>
						{isOver && <div className="dnd-highlighted" />}
						<div className={classNames('poster active')}>
							{!!video?.poster
								? <img className="poster-image" src={video.poster} alt={''} />
								: <div className="no-image-placeholder">
									<span style={colorStyles}>{video?.displayName}</span>
								</div>
							}
						</div>
					</>
				}
			</VideoContainer>
		</div>
    );
}
